import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Video } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatRankMetric } from '@/lib/ranks/formatRankMetric';
import { anonymousHallRow } from '@/lib/ranks/anonymousHallRow';
import { seasonWindowCopy } from '@/lib/ranks/seasonWindowCopy';
import { resolveNestLayout } from '@/lib/ranks/resolveNestLayout';
import { resolveRowFamilies } from '@/lib/ranks/resolveRowFamilies';
import type { ClipperRankFamily } from '@/lib/ranks/types';
import { buildHallStandingItems, type HallStandingEntry } from '@/lib/ranks/buildHallStanding';
import { RankFamilyNests } from '@/components/ranks/RankFamilyNests';
import { AnonymousClipperIdentity } from '@/components/ranks/AnonymousClipperIdentity';
import { ClipperLeaderboardAvatar } from '@/components/cpm/ClipperLeaderboardAvatar';
import { cn } from '@/lib/utils';

export type RankHallSeasonEntry = {
  rank: number;
  user_id?: string | null;
  name?: string | null;
  avatar_url?: string | null;
  anonymous: boolean;
  title_id?: string | null;
  views_delta: string;
  video_count: number;
  families?: ClipperRankFamily[];
};

export type RankHallClipper = {
  user_id?: string | null;
  name?: string | null;
  avatar_url?: string | null;
  anonymous: boolean;
  current_tier_ids: string[];
  families?: ClipperRankFamily[];
  lifetime_views: string;
  video_count: number;
};

export type RankHallSeason = {
  id: string;
  name: string;
  status: string;
  starts_at?: string | null;
  ends_at?: string | null;
  entries: RankHallSeasonEntry[];
};

export type RankHallPageInfo = {
  offset: number;
  limit: number;
  total: number;
};

export type RankHallSeasonOption = {
  id: string;
  name: string;
};

export type RankHallViewer = {
  user_id?: string | null;
  name?: string | null;
  avatar_url?: string | null;
};

export type RankHallProps = {
  season?: RankHallSeason | null;
  clippers: RankHallClipper[];
  seasonOptions: RankHallSeasonOption[];
  selectedSeasonId?: string;
  onSeasonChange: (seasonId: string) => void;
  viewer?: RankHallViewer | null;
};

const LIST_CLASS = 'rounded-xl border border-primary/25 overflow-hidden bg-card divide-y divide-primary/10';

function HallClipperStats({
  views,
  videoCount,
}: {
  views: string;
  videoCount: number;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-shrink-0 flex-col items-end gap-0.5 text-primary">
      <span
        data-testid="clipper-ranks-hall-views"
        className="inline-flex items-center gap-1 text-xs font-semibold"
      >
        <Users className="w-3 h-3" aria-hidden="true" />
        {t('cpmLeaderboard.views', { views })}
      </span>
      <span
        data-testid="clipper-ranks-hall-videos"
        className="inline-flex items-center gap-1 text-[11px] text-primary/80"
      >
        <Video className="w-3 h-3" aria-hidden="true" />
        {t('cpmLeaderboard.videos', { count: videoCount })}
      </span>
    </div>
  );
}

function rankLabel(
  rank: number | null | undefined,
  unranked: boolean | undefined,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (unranked) return t('cpmLeaderboard.unrankedRank');
  if (rank != null) return t('cpmLeaderboard.rank', { rank });
  return '';
}

function HallListRow({
  rank,
  anonymous,
  name,
  avatarUrl,
  views,
  videoCount,
  isViewer,
  unranked,
  children,
}: {
  rank?: number | null;
  anonymous: boolean;
  name: string | null;
  avatarUrl: string | null;
  views: string;
  videoCount: number;
  isViewer?: boolean;
  unranked?: boolean;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const label = rankLabel(rank, unranked, t);

  return (
    <li
      className={cn(
        'flex items-center gap-3 py-2.5 px-3',
        isViewer && 'bg-primary/15',
      )}
      aria-label={anonymous ? t('cpmLeaderboard.anonymousAria', { views }) : undefined}
      data-testid={
        isViewer
          ? 'clipper-ranks-viewer-row'
          : anonymous
            ? 'clipper-ranks-anonymous-row'
            : undefined
      }
    >
      {label ? (
        <span className="w-8 shrink-0 text-xs font-bold text-primary tabular-nums">
          {label}
        </span>
      ) : null}
      <ClipperLeaderboardAvatar
        name={name}
        avatarUrl={avatarUrl}
        anonymous={anonymous}
        anonymousTestId="clipper-ranks-anonymous-avatar"
      />
      {/* Below sm the medals get their own wrapping line under the name: kept
          inline and unshrinkable, a long collection overflowed onto the stats
          and squeezed the name to nothing on phones. */}
      <div
        data-testid="clipper-ranks-hall-identity"
        className="min-w-0 flex-1 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2"
      >
        <div className="flex min-w-0 items-center gap-2">
          {anonymous ? (
            <AnonymousClipperIdentity />
          ) : (
            <p className="min-w-0 truncate text-sm font-semibold text-primary">{name}</p>
          )}
          {isViewer ? (
            <span
              data-testid="clipper-ranks-you-badge"
              className="shrink-0 rounded-md border border-primary/50 bg-primary/30 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-primary"
            >
              {t('cpmLeaderboard.you')}
            </span>
          ) : null}
        </div>
        {children ? (
          <div className="flex min-w-0 flex-wrap items-center gap-1 sm:shrink-0">
            {children}
          </div>
        ) : null}
      </div>
      {unranked ? (
        <span className="text-[11px] font-semibold text-primary/80">
          {t('cpmLeaderboard.unranked')}
        </span>
      ) : (
        <HallClipperStats views={views} videoCount={videoCount} />
      )}
    </li>
  );
}

export type RankHallSeasonPanelProps = {
  season: RankHallSeason;
  seasonOptions?: RankHallSeasonOption[];
  selectedSeasonId?: string;
  onSeasonChange?: (seasonId: string) => void;
  viewer?: RankHallViewer | null;
};

/**
 * One season's standing (top 10, gap, viewer). Rendered inside the
 * season/lifetime tabs on /rankings and on its own on /earn, where only the
 * current season is shown and `seasonOptions` is omitted.
 */
export function RankHallSeasonPanel({
  season,
  seasonOptions = [],
  selectedSeasonId,
  onSeasonChange,
  viewer,
}: RankHallSeasonPanelProps) {
  const { t, i18n } = useTranslation();
  const selectValue = selectedSeasonId ?? season.id;
  const viewerUserId = viewer?.user_id ?? null;
  const windowCopy = seasonWindowCopy({
    status: season.status,
    startsAt: season.starts_at,
    endsAt: season.ends_at,
    language: i18n.language,
  });

  const seasonStanding = buildHallStandingItems({
    rows: (season.entries ?? []).map((entry) => ({
      user_id: entry.user_id,
      name: entry.name,
      avatar_url: entry.avatar_url,
      anonymous: entry.anonymous,
      views: entry.views_delta,
      video_count: entry.video_count,
      rank: entry.rank,
      title_id: entry.title_id,
      families: entry.families,
    })),
    viewerUserId,
    viewer,
  });

  return (
    <div className="space-y-3">
      {seasonOptions.length > 0 ? (
        <Tabs value={selectValue} onValueChange={onSeasonChange}>
          <TabsList
            data-testid="clipper-ranks-season-tabs"
            className="flex h-auto w-full flex-wrap justify-start sm:inline-flex sm:w-auto"
          >
            {seasonOptions.map((option) => (
              <TabsTrigger key={option.id} value={option.id}>
                {option.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      ) : (
        <h3 className="text-lg font-semibold">{season.name}</h3>
      )}
      {windowCopy ? (
        <p
          data-testid="clipper-ranks-season-window"
          className="text-sm text-muted-foreground"
        >
          {t(windowCopy.key, windowCopy.values)}
        </p>
      ) : null}
      <ol data-testid="clipper-ranks-season-list" className={LIST_CLASS}>
        {seasonStanding.map((item, index) => {
          if (item.type === 'gap') {
            return (
              <li
                key="gap"
                data-testid="clipper-ranks-gap"
                className="flex items-center justify-center py-2 text-sm font-bold tracking-[0.35em] text-primary/50"
                aria-hidden="true"
              >
                {t('cpmLeaderboard.gap')}
              </li>
            );
          }
          const display = anonymousHallRow({
            anonymous: !!item.entry.anonymous,
            user_id: item.entry.user_id,
            name: item.entry.name,
            avatar_url: item.entry.avatar_url,
            families: (item.entry as HallStandingEntry).families,
          });
          const views = formatRankMetric({
            trackId: 'views',
            metric: item.entry.total_views,
            language: i18n.language,
          });
          const titleId = (item.entry as HallStandingEntry).title_id ?? null;
          const layout = resolveNestLayout({
            surface: 'season',
            seasonStatus: season.status,
            hasSeasonTitle: Boolean(titleId),
          });
          return (
            <HallListRow
              key={`${item.entry.rank ?? 'unranked'}-${display.user_id ?? `anon-${index}`}`}
              rank={item.entry.rank}
              anonymous={display.anonymous}
              name={display.name}
              avatarUrl={display.avatar_url}
              views={views}
              videoCount={item.entry.video_count}
              isViewer={!!item.entry.is_viewer}
              unranked={!!item.entry.unranked}
            >
              <RankFamilyNests
                families={resolveRowFamilies({
                  families: display.families,
                  title_id: titleId,
                })}
                leadingFamilyId={layout.leadingFamilyId}
                labeledFamilyId={layout.labeledFamilyId}
                seasonTitleId={titleId}
                displayedSeasonId={season.id}
              />
            </HallListRow>
          );
        })}
      </ol>
    </div>
  );
}

export function RankHall({
  season,
  clippers,
  seasonOptions,
  selectedSeasonId,
  onSeasonChange,
  viewer,
}: RankHallProps) {
  const { t, i18n } = useTranslation();
  const viewerUserId = viewer?.user_id ?? null;
  const lifetimeLayout = resolveNestLayout({ surface: 'lifetime' });

  const seasonPanel = season ? (
    <RankHallSeasonPanel
      season={season}
      seasonOptions={seasonOptions}
      selectedSeasonId={selectedSeasonId}
      onSeasonChange={onSeasonChange}
      viewer={viewer}
    />
  ) : null;

  const lifetimeStanding = buildHallStandingItems({
    rows: clippers.map((clipper, index) => ({
      user_id: clipper.user_id,
      name: clipper.name,
      avatar_url: clipper.avatar_url,
      anonymous: clipper.anonymous,
      views: clipper.lifetime_views,
      video_count: clipper.video_count,
      rank: index + 1,
      current_tier_ids: clipper.current_tier_ids,
      families: clipper.families,
    })),
    viewerUserId,
    viewer,
  });

  const lifetimePanel = clippers.length > 0 ? (
    <div className="space-y-3">
      <ol data-testid="clipper-ranks-lifetime" className={LIST_CLASS}>
        {lifetimeStanding.map((item, index) => {
          if (item.type === 'gap') {
            return (
              <li
                key="gap"
                data-testid="clipper-ranks-gap"
                className="flex items-center justify-center py-2 text-sm font-bold tracking-[0.35em] text-primary/50"
                aria-hidden="true"
              >
                {t('cpmLeaderboard.gap')}
              </li>
            );
          }
          const display = anonymousHallRow({
            anonymous: !!item.entry.anonymous,
            user_id: item.entry.user_id,
            name: item.entry.name,
            avatar_url: item.entry.avatar_url,
            current_tier_ids: (item.entry as HallStandingEntry).current_tier_ids,
            families: (item.entry as HallStandingEntry).families,
          });
          const views = formatRankMetric({
            trackId: 'views',
            metric: item.entry.total_views,
            language: i18n.language,
          });
          return (
            <HallListRow
              key={item.entry.is_viewer ? 'viewer' : display.user_id ?? `anon-${index}`}
              rank={item.entry.rank}
              anonymous={display.anonymous}
              name={display.name}
              avatarUrl={display.avatar_url}
              views={views}
              videoCount={item.entry.video_count}
              isViewer={!!item.entry.is_viewer}
              unranked={!!item.entry.unranked}
            >
              <RankFamilyNests
                families={resolveRowFamilies({
                  families: display.families,
                  current_tier_ids: display.current_tier_ids,
                })}
                leadingFamilyId={lifetimeLayout.leadingFamilyId}
                labeledFamilyId={lifetimeLayout.labeledFamilyId}
              />
            </HallListRow>
          );
        })}
      </ol>
    </div>
  ) : null;

  const showTabs = Boolean(seasonPanel && lifetimePanel);

  return (
    <div className="space-y-6">
      {showTabs ? (
        <Tabs defaultValue="season">
          <TabsList data-testid="clipper-ranks-hall-tabs" className="grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
            <TabsTrigger value="season">{t('ranks.tabs.season')}</TabsTrigger>
            <TabsTrigger value="lifetime">{t('ranks.tabs.lifetime')}</TabsTrigger>
          </TabsList>
          <TabsContent value="season">{seasonPanel}</TabsContent>
          <TabsContent value="lifetime">{lifetimePanel}</TabsContent>
        </Tabs>
      ) : (seasonPanel ?? lifetimePanel)}
    </div>
  );
}
